import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOptions,
  DialogOptionsItem,
} from "@prismicio/editor-ui"
import { type ReactNode, useState } from "react"

import type { CropperImage } from "../../../core/imageUtils"
import { type CroppedImage, initCroppedImage } from "./croppedImage"
import { CropperControls } from "./CropperControls"

interface CropperDialogProps {
  trigger: React.ReactNode
  /** Flattened list of all thumbnails in the field */
  initialImages: CropperImage[]
  /** Index of the initial image displayed in the cropper */
  initialOption: number
  onClose: (result: { croppedImages: CroppedImages; option: number }) => void
  renderTooltip: (trigger: ReactNode) => ReactNode
}

export function CropperDialog(props: CropperDialogProps) {
  const { trigger, initialImages, renderTooltip } = props

  const {
    open,
    onOpenChange,
    ready,
    option,
    selectedImage,
    croppedImage,
    setCroppedImage,
    setReady,
    setOption,
  } = useCropperDialog(props)

  return (
    <Dialog
      trigger={trigger}
      onAnimationEnd={() => setReady(true)}
      open={open}
      onOpenChange={onOpenChange}
      renderTooltip={renderTooltip}
    >
      <DialogHeader icon="photo" title="Image editor" />
      <DialogDescription hidden>
        Crop your image to the desired size.
      </DialogDescription>

      <DialogOptions
        value={String(option)}
        onValueChange={(newOption) => setOption(Number(newOption))}
      >
        {initialImages.map((image, index) => (
          <DialogOptionsItem
            key={image.thumbnail.name}
            value={String(index)}
            icon="photo"
            title={image.labels[0] ?? "Main"}
            subtitle={image.labels[1] ?? ""}
          />
        ))}
      </DialogOptions>

      {selectedImage && (
        <DialogContent>
          {ready && croppedImage && (
            <CropperControls
              // Reset when the selected image changes.
              key={selectedImage.thumbnail.name}
              selectedImage={selectedImage}
              imageCrop={croppedImage}
              setImageCrop={setCroppedImage}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  )
}

export type CroppedImages = Record<string, CroppedImage>

function useCropperDialog(params: CropperDialogProps) {
  const { initialImages, initialOption, onClose } = params

  const [croppedImages, setCroppedImages] =
    useState<CroppedImages>(getInitialCrops)

  // Dialog
  const [open, setOpen] = useState(false)
  const [ready, setReady] = useState(false)
  const [option, setOption] = useState(initialOption)

  function onOpenChange(isOpen: boolean) {
    if (isOpen) {
      // Make sure the content is updated when open.
      setOption(initialOption)
      setCroppedImages(getInitialCrops())
    } else {
      // Notify the parent with the result when closed.
      setReady(false)

      onClose({
        option,
        croppedImages,
      })
    }
    setOpen(isOpen)
  }

  const selectedImage = initialImages[option]
  const croppedImage = croppedImages[selectedImage?.thumbnail.name ?? ""]

  function setCroppedImage(croppedImage: CroppedImage) {
    if (!selectedImage) return
    const name = selectedImage.thumbnail.name
    setCroppedImages({
      ...croppedImages,
      [name]: croppedImage,
    })
  }

  function getInitialCrops() {
    return initialImages.reduce<CroppedImages>((croppedImages, image) => {
      // If there is no image content, we can't initialize the crop
      if (!image.contentView) return croppedImages

      croppedImages[image.thumbnail.name] = initCroppedImage(image.contentView)

      return croppedImages
    }, {})
  }

  return {
    open,
    ready,
    option,
    selectedImage,
    croppedImage,
    setCroppedImage,
    setReady,
    setOption,
    onClose,
    onOpenChange,
  }
}
